import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'

import { SidebarViewState } from 'apps/aiAutomation/components/Layout/lib/types'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import {
  AiAutomationLayoutActions,
  AiAutomationLayoutSelectors,
} from 'apps/aiAutomation/store/layout'

export const useViewState = (viewId: SidebarViewType) => {
  const dispatch = useAppDispatch()

  const viewState = useAppSelector((state) =>
    AiAutomationLayoutSelectors.getViewState(state, viewId),
  )
  const interactiveView = useAppSelector(AiAutomationLayoutSelectors.getInteractiveView)

  const [prevUniqueState, setPrevUniqueState] = useState<SidebarViewState | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  const prevStateRef = useRef<SidebarViewState | null>(null)

  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const makeViewInteractable = useCallback(() => {
    dispatch(AiAutomationLayoutActions.interactiveViewUpdated(viewId))
  }, [dispatch, viewId])

  const resetInteractableView = useCallback(() => {
    dispatch(AiAutomationLayoutActions.interactiveViewUpdated(null))
  }, [dispatch])

  useEffect(() => {
    if (prevStateRef.current !== null && prevStateRef.current !== viewState) {
      setPrevUniqueState(prevStateRef.current)
    }

    prevStateRef.current = viewState
  }, [viewState])

  useEffect(() => {
    if (viewState === 'closed' && !prevUniqueState) {
      return
    }

    if ((viewState === 'closed' || viewState === 'hidden') && prevUniqueState === 'open') {
      resetInteractableView()
    }

    if (viewState === 'open' && prevUniqueState === 'closed') {
      setIsVisible(true)
    }

    if (viewState === 'closed' && prevUniqueState === 'hidden') {
      setIsVisible(false)
    }
  }, [prevUniqueState, resetInteractableView, viewId, viewState])

  useEffect(() => {
    if (!isVisible || !wrapperRef.current) {
      return
    }

    const element = wrapperRef.current

    const onAnimationEnd = (event: AnimationEvent) => {
      if (event.target !== wrapperRef.current) {
        return
      }

      if (viewState === 'closed') {
        setIsVisible(false)

        dispatch(AiAutomationLayoutActions.viewPayloadUpdated({ view: viewId, payload: null }))
      }

      if (viewState === 'open') {
        makeViewInteractable()
      }
    }

    element.addEventListener('animationend', onAnimationEnd)

    return () => {
      element.removeEventListener('animationend', onAnimationEnd)
    }
  }, [dispatch, isVisible, makeViewInteractable, setIsVisible, viewId, viewState])

  return {
    viewState,
    prevUniqueState,

    isInteractable: interactiveView === viewId,

    isVisible,

    wrapperRef,
  }
}
